'use strict';

/**
 * OnboardingModule — post-registration merchant KYC/business onboarding:
 * business profile completion, address capture, business-type selection,
 * compliance document upload, and final submission. Unlike
 * RegistrationModule (a vendor onboarding a *new* merchant), every call
 * here is made with the merchant's *own* clientId/key — the same
 * already-authenticated, encrypted gateway pipeline every other module in
 * this SDK uses (paymentSystem resolves req.user.merchantId from that
 * clientId, exactly like AepsModule/DmtModule's onboarding calls).
 * Mirrors payapp's composables/apis/useOnboadingApi.ts and
 * components/OnboardingNew.vue.
 */
class OnboardingModule {
  constructor(client) {
    this._c = client;
  }

  _post(path, body = {}) { return this._c.post(path, body); }
  _get(path, body = {})  { return this._c.get(path, body); }
  _put(path, body = {})  { return this._c.put(path, body); }

  /** The authenticated merchant's own profile — used to pre-fill contact fields. */
  getProfile()          { return this._get('merchant/me'); }

  /** Self-service — resolves the merchant from the caller's own auth, richer
   *  than getProfile(): includes `id` (the real merchant id, not the `mid`
   *  display code), `business_type`, and — once verified — `pan`/`pan_name`
   *  (NOT `pan_dob`/`pan_father_name`; paymentSystem's PAN-verify upsert
   *  never persists those two, so they stay null even after a successful
   *  verification). Mirrors webpayment's useIsgOnboardingApi.ts
   *  #isgGetOwnMerchantForOnboarding — the profile source the real
   *  /merchant/onboarding/isg screen actually uses, not merchant/me.
   *  @returns {Promise<{statusCode, data: {id, business_type, pan, pan_name, ...}}>} */
  getOwnMerchantForOnboarding() { return this._get('onboarding/merchants/onboarding'); }

  /**
   * Sends a mobile OTP. paymentSystem's /send-otp and /verify-otp don't
   * resolve identity from the caller's clientId at all (they're plain,
   * unauthenticated-by-design routes keyed purely on phone+otp — see
   * server.js's publicPaths) — going through this module's normal
   * authenticated pipeline still works (the gateway's own X-Client-Id check
   * just has to pass; paymentSystem itself never looks at it here), it's
   * just not what actually confirms identity. That's the OTP round-trip
   * itself: a confirmation gate mirroring payapp's pages/index.vue login,
   * not a replacement for the merchant's own clientId/key used everywhere
   * else in this module.
   * @param {{phone: string, email?: string}} payload
   */
  sendOtp(payload)      { return this._post('send-otp', payload); }

  /** @param {{phone: string, otp: string}} payload
   *  @returns {Promise<{statusCode, data: {token: string|null, role?, user?, merchant?}}>} -
   *  `data.token: null` (with statusCode '00') means the OTP matched but no
   *  account exists for that mobile number. */
  verifyOtp(payload)    { return this._post('verify-otp', payload); }

  /** @param {string} q - server ignores/short-circuits queries shorter than 2 chars */
  searchMcc(q)           { return this._get('onboading/search/mcc', { q }); }

  /** @param {string} q */
  searchPincode(q)       { return this._get('onboading/search/pincode', { q }); }

  /** @returns {Promise<{statusCode, data: {id, label, value, status}[]}>} */
  getBusinessTypes()    { return this._get('onboading/businessType'); }

  /** @returns {Promise<{statusCode, data: {id, value}[]}>} */
  getTurnoverRanges()   { return this._get('onboading/trunOver'); }

  /** Every business type with its required document categories/docs — drives
   *  the document-upload step's dynamic requirements list.
   *  @returns {Promise<{statusCode, data: {id, type, value, categories: {category, documents: {code, name, description}[]}[]}[]}>} */
  getComplianceCatalog() { return this._get('onboading/compliance'); }

  /** @param {{pan: string, name: string, dob: string, fathername?: string}} payload */
  verifyPan(payload)     { return this._post('onboading/verify/pan', payload); }

  /** @param {{accountNumber: string, accountHolderName: string, ifsc: string, additionalData?: boolean, consent?: string}} payload */
  verifyAccount(payload) { return this._post('onboading/verify/account', payload); }

  /**
   * Best-effort service-KYC creation — paymentSystem's handler dereferences
   * merchant.merchantKyc.id with no null-check, so this must be called
   * *after* the merchant already has a MerchantKyc row (isgVerifyPan creates
   * one if missing; calling this first on a brand-new merchant throws).
   * A repeat call for an already-existing row comes back as HTTP 200 with
   * `{statusCode:'500', message:'Service KYC already exists for this
   * merchant and interface'}` — that specific message means "already set
   * up", not failure; treat only anything else as a real error.
   * @param {{merchantId: string, service: string, interfaceName: string}} payload
   */
  createServiceKyc(payload) { return this._post(`services/createServceKyc/${payload.merchantId}`, payload); }

  /**
   * Marks a merchant's ISG onboarding OTP step as confirmed. paymentSystem
   * always replies statusCode:'00' here — even when the update silently
   * no-ops (e.g. no MerchantServiceKyc row yet for this interface) — so
   * there is no failure signal to check; fire-and-forget.
   * @param {{merchantId: string, interfaceName?: string}} payload
   */
  setVerifyOtp(payload) { return this._post('onboarding/upi/isg/set-verify-otp', payload); }

  /**
   * ISG-specific PAN verification (distinct from the generic verifyPan()
   * above — this is what webpayment's actual /merchant/onboarding/isg screen
   * calls). Creates the merchant's MerchantKyc/MerchantServiceKyc rows if
   * missing, so this is safe to call as the very first onboarding.* action
   * for a brand-new merchant.
   * @param {{pan: string, name: string, dob: string, fathername: string, merchantId?: string, interface?: string}} payload
   * @returns {Promise<{statusCode, message, data?: {pan, pan_name, entity_pan}}>}
   */
  isgVerifyPan(payload) { return this._post('onboarding/upi/isg/verify/pan', payload); }

  /**
   * ISG-specific bank account verification. Call isgVerifyPan (or otherwise
   * ensure the interface's MerchantServiceKyc row already exists) first —
   * otherwise a *successful* verification can still surface as an HTTP 500
   * here (paymentSystem's status-writer throws after the account details
   * are already persisted, so don't assume a 500 means the account wasn't
   * actually verified — check isgComplianceStatus()'s kycStatus.bank before
   * retrying).
   * @param {{accountHolderName: string, accountNumber: string, ifsc: string, consent: string, additionalData: string, merchantId?: string, interface?: string}} payload
   * @returns {Promise<{statusCode, message, data?: {account_holder_name, bank_name, bank_ifsc_code, account_holder_score, account_holder_result}}>}
   */
  isgVerifyAccount(payload) { return this._post('onboarding/upi/isg/verify/account', payload); }

  /**
   * Per-category/per-document compliance status for the ISG interface —
   * every count (required/uploaded/compliant/missing) is fully computed
   * server-side from the business-type document matrix; never recompute
   * these client-side.
   * @param {{interfaceName?: string}} [payload]
   * @returns {Promise<{statusCode, kycStatus: {otp, pan, bank, aadhaar, storeImage},
   *   summary: {required, uploaded, missing, compliant},
   *   categories: {category, required, uploaded, missing, compliant,
   *     documents: {code, name, uploaded, mandatory, group}[]}[]}>}
   */
  isgComplianceStatus(payload = {}) { return this._get('onboarding/upi/isg/compliance/status', { interface: 'ISG', ...payload }); }

  /**
   * Links already-uploaded image ids (see uploadComplianceImage) to a
   * document type — the ISG-specific counterpart to initCompliance() below,
   * without the pan/gst/settlement metadata payload (this route only does
   * the doc/image-linking portion). `images` must carry >=2 ids when
   * `doc_type === 'AADHAAR'` exactly (paymentSystem hard-validates that
   * one case); every other doc type just needs a non-empty array.
   * @param {{doc_type: string, doc_number: string, images: number[], merchantId?: string, interface?: string}} payload
   */
  isgDocUpload(payload) { return this._post('onboarding/upi/isg/compliance/docUpload', payload); }

  /**
   * Marks the merchant's KYC as submitted (mstatus -> SUBMITTED) — a pure
   * status flip with no compliance/document validation and no external
   * interface call. This is the safe way to finish ISG onboarding;
   * paymentSystem's own /onboarding/upi/isg/submit/onboading route is
   * fragile (dereferences several merchant/address/PAN/bank fields with no
   * null-checks and fires a live call to the ISG partner API) and
   * webpayment's own /merchant/onboarding/isg screen deliberately avoids it
   * in favor of this endpoint — mirrored here for the same reason, not a
   * shortcut.
   * @param {{merchantId?: string}} [payload] - ignored for a merchant-role
   *   caller (forced to their own id server-side); required for a vendor
   *   acting on a merchant's behalf.
   * @returns {Promise<{statusCode, message, data: {id, mid, status, mstatus}}>}
   */
  markKycSubmitted(payload = {}) { return this._post('merchant/kyc/submit', payload); }

  /** @param {{businessTypeCode: string}} payload — returns {success, message, merchantId, businessType} (not statusCode) */
  updateBusinessType(payload) { return this._put('onboarding/business-type', payload); }

  /**
   * Uploads one compliance document image/PDF, base64-encoded — fintech-
   * gateway's encrypted JSON envelope can't carry true multipart/form-data,
   * so this goes through a base64 fallback paymentSystem's
   * /onboarding/compliance/images route added alongside its existing
   * multipart path (same validation, same upload-to-Spaces destination).
   * @param {{file: string, filename: string, mimetype: string, docid?: number}} payload -
   *   `file` is base64 (a data: URI is also accepted — the prefix is stripped server-side)
   * @returns {Promise<{statusCode, data: {id, url, key, filename, mimetype, size}}>}
   */
  uploadComplianceImage(payload) { return this._post('onboarding/compliance/images', payload); }

  /** Attaches an uploaded image (by id) to a document type and records its
   *  metadata (PAN/GST/bank details, doc number). Mirrors payapp's
   *  DOC_RULES[type].buildPayload() shapes exactly.
   *  @param {{doc_type: string, doc_number: string, images: number[], pan?: object, gst?: object, settlement?: object}} payload */
  initCompliance(payload) { return this._post('onboarding/init-compliance', payload); }

  /**
   * Final onboarding submission — profile + all three addresses in one call.
   * NOTE: paymentSystem's /Onboarding handler unconditionally attempts to
   * register a brand-new user from this payload before falling back to the
   * caller's own authenticated merchantId (onboarding/controllers/
   * controller.js#Onboarding -> userRegistor()), and that pre-check rejects
   * with "Email already registered" whenever primary_email_id/primary_mobile
   * already belong to an existing user — which, for the normal register-then-
   * onboard flow this widget mirrors, is *always* the calling merchant
   * themselves. This is payapp's/webpayment's own existing behavior, not
   * something introduced here — surfaced in case a "successful registration,
   * failed onboarding" combination looks confusing downstream.
   * @param {object} payload - see webpayment/payapp's onboarding submit for the full field shape
   */
  submitOnboarding(payload) { return this._post('onboarding/Onboarding', payload); }
}

module.exports = { OnboardingModule };
